/* ============================================================
   redline.ink — client cryptography

   AES-256-GCM via Web Crypto (crypto.subtle), empty AAD.

   Wire format (matches helpers/aes_gcm.py and docs/api-contract.md):
     blob = nonce(12) || ciphertext || tag(16)
   Algorithm: AES-256-GCM, 32-byte key, 12-byte random nonce,
   empty AAD. The key is base64url'd into the URL fragment and
   never sent to the server — that is the zero-knowledge guarantee.

   URL shapes:
     reader:           /plan/<slug>#k=<base64url(key)>
     with-comments:    /plan/<slug>#k=<base64url(key)>&c=<base64url(deflate-raw(commentsJSON))>

   Kept deliberately readable (un-minified ethos): no clever tricks,
   one concern per function, the wire layout spelled out in constants.
   ============================================================ */

import type { Comment, Reply, ReviewDoc } from '@/types'

const KEY_BYTES = 32 // AES-256
const NONCE_BYTES = 12 // GCM standard nonce
const TAG_BYTES = 16 // GCM auth tag
const TAG_BITS = TAG_BYTES * 8

/**
 * Hard ceiling on an inflated `&c=` comment bundle. The handoff modal caps
 * share bundles at 14 KB *compressed*, and deflate tops out around 1000:1, so
 * a legitimate bundle can never come near this — but the fragment arrives in
 * a link from anyone, and without a ceiling a crafted `&c=` could
 * decompress-bomb the reader's tab. 4 MB is comfortably above any real
 * bundle and comfortably below "tab locks up".
 */
const MAX_INFLATED_BYTES = 4 * 1024 * 1024

/** Raised by `parseHash`/`decrypt` when a fragment or blob is malformed. */
export class CryptoError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CryptoError'
  }
}

/** A freshly generated 32-byte AES-256 key. */
export function generateKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(KEY_BYTES))
}

/** Import raw key bytes as a non-extractable AES-GCM CryptoKey. */
async function importKey(key: Uint8Array, usage: KeyUsage): Promise<CryptoKey> {
  if (key.length !== KEY_BYTES) {
    throw new CryptoError(
      'redline crypto: key must be ' + KEY_BYTES + ' bytes, got ' + key.length,
    )
  }
  return crypto.subtle.importKey('raw', toArrayBuffer(key), 'AES-GCM', false, [usage])
}

/**
 * Encrypt `plaintext` under `key` with a fresh random 12-byte nonce.
 * Returns the wire blob `nonce || ciphertext || tag`.
 */
export async function encrypt(key: Uint8Array, plaintext: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await importKey(key, 'encrypt')
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES))

  const sealed = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, tagLength: TAG_BITS },
    cryptoKey,
    toArrayBuffer(plaintext),
  )
  // Web Crypto returns ciphertext || tag; prepend the nonce.
  const ctWithTag = new Uint8Array(sealed)
  const blob = new Uint8Array(NONCE_BYTES + ctWithTag.length)
  blob.set(nonce, 0)
  blob.set(ctWithTag, NONCE_BYTES)
  return blob
}

/**
 * Decrypt a wire blob (`nonce || ciphertext || tag`) under `key`. Throws
 * `CryptoError` if the GCM tag does not verify (tampered / wrong key).
 */
export async function decrypt(key: Uint8Array, blob: Uint8Array): Promise<Uint8Array> {
  if (blob.length < NONCE_BYTES + TAG_BYTES) {
    throw new CryptoError(
      'redline crypto: blob too short (' +
        blob.length +
        ' bytes; need at least ' +
        (NONCE_BYTES + TAG_BYTES) +
        ')',
    )
  }
  const cryptoKey = await importKey(key, 'decrypt')
  const nonce = blob.subarray(0, NONCE_BYTES)
  const ctWithTag = blob.subarray(NONCE_BYTES)

  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce, tagLength: TAG_BITS },
      cryptoKey,
      toArrayBuffer(ctWithTag),
    )
    return new Uint8Array(plaintext)
  } catch {
    // Web Crypto throws an opaque OperationError on tag mismatch.
    throw new CryptoError('redline crypto: decryption failed (bad key or tampered ciphertext)')
  }
}

/* ---- base64url (no padding, tolerant decode) ----------------- */

/** base64url-encode (no padding). */
export function base64urlEncode(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** base64url-decode (tolerates missing padding and standard base64 chars). */
export function base64urlDecode(s: string): Uint8Array {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/')
  // Restore padding to a multiple of 4 so atob is happy.
  const remainder = b64.length % 4
  if (remainder === 2) b64 += '=='
  else if (remainder === 3) b64 += '='
  else if (remainder === 1) {
    throw new CryptoError('redline crypto: invalid base64url length')
  }
  let binary: string
  try {
    binary = atob(b64)
  } catch {
    throw new CryptoError('redline crypto: invalid base64url')
  }
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/* ---- deflate-raw compression (CompressionStream) ------------- */

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([toArrayBuffer(bytes)])
    .stream()
    .pipeThrough(new CompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/* ---- URL fragment parse / build ------------------------------ */

/** Parsed contents of a `/plan/<slug>#...` URL fragment. */
export interface ParsedHash {
  /** The decryption key, or null if `k=` was absent/malformed. */
  key: Uint8Array | null
  /** Bundled reviewer comments (from `&c=`), or null if absent. */
  comments: Comment[] | null
}

/**
 * Parse a URL fragment of the form `#k=<key>&c=<comments>` into its parts.
 * Never throws for an empty/keyless fragment — returns `{ key: null }`.
 * Throws `CryptoError` only when a present `k=` decodes to the wrong byte
 * length (malformed key bytes).
 *
 * The frozen contract types this as a synchronous function returning a
 * resolved `Comment[]`, but the only built-in `deflate-raw` decoder
 * (`DecompressionStream`) is async. To honor the signature without diverging
 * it, the `&c=` bundle is inflated inline via a small synchronous RFC-1951
 * raw-inflate (`inflateRawSync` below). Bundles are <14 KB by design (the
 * handoff hard cap), so the inline decoder is trivial work.
 */
export function parseHash(hash: string): ParsedHash {
  const params = parseFragmentParams(hash)

  const kParam = params.get('k')
  let key: Uint8Array | null = null
  if (kParam) {
    const decoded = base64urlDecode(kParam)
    if (decoded.length !== KEY_BYTES) {
      throw new CryptoError(
        'redline crypto: key in fragment is ' +
          decoded.length +
          ' bytes, expected ' +
          KEY_BYTES,
      )
    }
    key = decoded
  }

  let comments: Comment[] | null = null
  const cParam = params.get('c')
  if (cParam) {
    const compressed = base64urlDecode(cParam)
    const json = inflateRawSync(compressed)
    let parsed: unknown
    try {
      parsed = JSON.parse(new TextDecoder().decode(json))
    } catch {
      throw new CryptoError('redline crypto: comment bundle is not valid JSON')
    }
    comments = validateComments(parsed)
  }

  return { key, comments }
}

/**
 * Build a `Comment` from a decoded record, defaulting the positional anchor
 * fields. Unknown keys and transient `_`-state are dropped — only the persisted
 * shape survives. The positional fields (`sourceLine*`/`domRange*`) default to 0
 * because the agent seeding contract OMITS them (the reader re-anchors seeded
 * comments by `quotedText`, not offsets); a missing one is normal, not
 * malformed. Callers gate which records reach here (see {@link validateComments}).
 */
function coerceComment(c: Record<string, unknown>): Comment {
  return {
    id: c.id as string,
    sourceLineStart: typeof c.sourceLineStart === 'number' ? c.sourceLineStart : 0,
    sourceLineEnd: typeof c.sourceLineEnd === 'number' ? c.sourceLineEnd : 0,
    quotedText: typeof c.quotedText === 'string' ? c.quotedText : '',
    blockHint: typeof c.blockHint === 'string' ? c.blockHint : '',
    domRangeStart: typeof c.domRangeStart === 'number' ? c.domRangeStart : 0,
    domRangeEnd: typeof c.domRangeEnd === 'number' ? c.domRangeEnd : 0,
    note: typeof c.note === 'string' ? c.note : '',
    createdAt: typeof c.createdAt === 'number' ? c.createdAt : 0,
    // Self-asserted author snapshot. Validated shape only.
    ...(isAuthor(c.author) ? { author: { id: c.author.id, name: c.author.name } } : {}),
    // Thread fields (1.3.0): replies validated element-wise (a malformed reply
    // is dropped, never thrown — same lenient rationale as the caller);
    // `resolved` requires a usable {at, by}.
    ...(Array.isArray(c.replies) ? { replies: coerceReplies(c.replies) } : {}),
    ...(isResolved(c.resolved) ? { resolved: { at: c.resolved.at, by: c.resolved.by } } : {}),
  }
}

/** Validate replies element-wise into well-formed `Reply[]`; bad entries drop. */
function coerceReplies(parsed: unknown[]): Reply[] {
  const out: Reply[] = []
  for (const entry of parsed) {
    const r = entry as Record<string, unknown>
    if (
      r &&
      typeof r === 'object' &&
      typeof r.id === 'string' &&
      typeof r.note === 'string' &&
      isAuthor(r.author)
    ) {
      out.push({
        id: r.id,
        author: { id: r.author.id, name: r.author.name },
        note: r.note,
        createdAt: typeof r.createdAt === 'number' ? r.createdAt : 0,
      })
    }
  }
  return out
}

/** Narrow an unknown to a well-formed `{ at, by }` resolved marker. */
function isResolved(v: unknown): v is { at: number; by: string } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Record<string, unknown>).at === 'number' &&
    typeof (v as Record<string, unknown>).by === 'string'
  )
}

/**
 * Validate a decoded comment array into a `Comment[]`.
 *
 * Two callers, two trust levels:
 *  - **strict (default)** — the `&c=` URL bundle, which rides *outside* the GCM
 *    envelope and has no integrity protection. Every field is checked and any
 *    malformed entry throws `CryptoError`, mirroring how a tampered ciphertext
 *    fails loudly.
 *  - **lenient** — the encrypted agent-seeded snapshot. It may omit positional
 *    anchor fields because the reader re-anchors by quoted text. Require only a
 *    usable thread id and default the rest; malformed records are dropped.
 */
function validateComments(parsed: unknown, lenient = false): Comment[] {
  if (!Array.isArray(parsed)) {
    throw new CryptoError('redline crypto: comment bundle is not an array')
  }
  if (lenient) {
    return parsed
      .filter(
        (c): c is Record<string, unknown> =>
          typeof c === 'object' && c !== null && typeof (c as Record<string, unknown>).id === 'string',
      )
      .map(coerceComment)
  }
  return parsed.map((entry) => {
    const c = entry as Record<string, unknown>
    if (
      typeof c !== 'object' ||
      c === null ||
      typeof c.id !== 'string' ||
      typeof c.sourceLineStart !== 'number' ||
      typeof c.sourceLineEnd !== 'number' ||
      typeof c.quotedText !== 'string' ||
      typeof c.blockHint !== 'string' ||
      typeof c.domRangeStart !== 'number' ||
      typeof c.domRangeEnd !== 'number' ||
      typeof c.note !== 'string' ||
      typeof c.createdAt !== 'number'
    ) {
      throw new CryptoError('redline crypto: malformed comment in bundle')
    }
    return coerceComment(c)
  })
}

/** Narrow an unknown to a well-formed `{ id, name }` author object. */
function isAuthor(a: unknown): a is { id: string; name: string } {
  return (
    typeof a === 'object' &&
    a !== null &&
    typeof (a as Record<string, unknown>).id === 'string' &&
    typeof (a as Record<string, unknown>).name === 'string'
  )
}

/**
 * Encode a comment bundle to the `c=` fragment value:
 * `base64url(deflate-raw(JSON))`. This is the exact string whose byte length
 * legacy links and portable HTML snapshots. Returns `''` for an empty bundle.
 */
export async function encodeComments(comments: Comment[]): Promise<string> {
  if (!comments || comments.length === 0) return ''
  const json = new TextEncoder().encode(JSON.stringify(comments))
  const compressed = await deflateRaw(json)
  return base64urlEncode(compressed)
}

/* ---- review companion blob (encrypted comments at rest) ------ */

/**
 * Encrypt an immutable thread snapshot into the review wire blob
 * (`nonce || ct || tag`) under the plan key — the create-only body PUT to
 * `/api/review/<slug>`.
 */
export async function encodeReviewBlob(key: Uint8Array, doc: ReviewDoc): Promise<Uint8Array> {
  const payload = { comments: doc.comments ?? [] }
  const json = new TextEncoder().encode(JSON.stringify(payload))
  return encrypt(key, json)
}

/**
 * Decrypt + validate an immutable review snapshot. A bare `Comment[]` is the
 * legacy v1 shape. Rides inside the GCM envelope, so a
 * tampered blob fails the tag check in `decrypt`. Throws `CryptoError` on a bad
 * key / tampered blob / malformed JSON.
 */
export async function decodeReviewBlob(key: Uint8Array, blob: Uint8Array): Promise<ReviewDoc> {
  const plaintext = await decrypt(key, blob)
  let parsed: unknown
  try {
    parsed = JSON.parse(new TextDecoder().decode(plaintext))
  } catch {
    throw new CryptoError('redline crypto: review blob is not valid JSON')
  }
  if (Array.isArray(parsed)) {
    return { comments: validateComments(parsed, true) }
  }
  if (typeof parsed === 'object' && parsed !== null) {
    const obj = parsed as Record<string, unknown>
    return {
      comments: validateComments(Array.isArray(obj.comments) ? obj.comments : [], true),
    }
  }
  throw new CryptoError('redline crypto: review blob has an unexpected shape')
}

/**
 * Build the fragment (without the leading `#`) for a share URL: always the
 * key, plus the compressed comment bundle when `comments` is provided.
 */
export async function buildHash(key: Uint8Array, comments?: Comment[]): Promise<string> {
  if (key.length !== KEY_BYTES) {
    throw new CryptoError(
      'redline crypto: key must be ' + KEY_BYTES + ' bytes, got ' + key.length,
    )
  }
  let fragment = 'k=' + base64urlEncode(key)

  if (comments && comments.length > 0) {
    fragment += '&c=' + (await encodeComments(comments))
  }

  return fragment
}

/* ---- fragment param helper ----------------------------------- */

function parseFragmentParams(hash: string): URLSearchParams {
  // Tolerate a leading '#', a full URL with a '#', or a bare 'k=...&c=...'.
  let frag = hash
  const hashIndex = frag.indexOf('#')
  if (hashIndex >= 0) frag = frag.slice(hashIndex + 1)
  return new URLSearchParams(frag)
}

/* ---- helpers ------------------------------------------------- */

/**
 * Return an ArrayBuffer view backing `bytes`. Web Crypto / Blob accept
 * BufferSource; we copy into a tight ArrayBuffer to avoid passing a view
 * with a nonzero byteOffset or a shared/oversized buffer.
 */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.length)
  copy.set(bytes)
  return copy.buffer
}

/* ---- inline synchronous raw-inflate (RFC 1951) ---------------

   parseHash must be synchronous per the frozen contract, but the only
   built-in deflate-raw decoder (DecompressionStream) is async. Rather
   than diverge the signature, we ship a small, readable, dependency-free
   raw-inflate so the sync `parseHash` can resolve comments inline. It is
   correctness-focused, not speed-focused; comment bundles are <14 KB by
   design (the handoff hard cap), so this is trivial work.
--------------------------------------------------------------- */

function inflateRawSync(input: Uint8Array): Uint8Array {
  const out: number[] = []
  let bytePos = 0
  let bitBuf = 0
  let bitCnt = 0

  // Every output byte funnels through here so a hostile stream can't inflate
  // past the bundle ceiling (decompress-bomb guard; see MAX_INFLATED_BYTES).
  function emit(byte: number): void {
    if (out.length >= MAX_INFLATED_BYTES) {
      throw new CryptoError(
        'redline crypto: comment bundle inflates past ' + MAX_INFLATED_BYTES + ' bytes',
      )
    }
    out.push(byte)
  }

  function getBit(): number {
    if (bitCnt === 0) {
      if (bytePos >= input.length) {
        throw new CryptoError('redline crypto: truncated deflate stream')
      }
      bitBuf = input[bytePos++]
      bitCnt = 8
    }
    const bit = bitBuf & 1
    bitBuf >>= 1
    bitCnt--
    return bit
  }

  function getBits(n: number): number {
    let value = 0
    for (let i = 0; i < n; i++) {
      value |= getBit() << i
    }
    return value
  }

  // Build a canonical Huffman decoder from a list of code lengths.
  function buildHuffman(lengths: number[]): { counts: number[]; symbols: number[] } {
    const MAX_BITS = 15
    const counts = new Array(MAX_BITS + 1).fill(0)
    for (const len of lengths) counts[len]++
    counts[0] = 0
    const offsets = new Array(MAX_BITS + 1).fill(0)
    for (let i = 1; i < MAX_BITS; i++) {
      offsets[i + 1] = offsets[i] + counts[i]
    }
    const symbols = new Array(lengths.length).fill(0)
    for (let sym = 0; sym < lengths.length; sym++) {
      if (lengths[sym] !== 0) {
        symbols[offsets[lengths[sym]]++] = sym
      }
    }
    return { counts, symbols }
  }

  function decodeSymbol(huff: { counts: number[]; symbols: number[] }): number {
    let code = 0
    let first = 0
    let index = 0
    for (let len = 1; len <= 15; len++) {
      code |= getBit()
      const count = huff.counts[len]
      if (code - first < count) {
        return huff.symbols[index + (code - first)]
      }
      index += count
      first += count
      first <<= 1
      code <<= 1
    }
    throw new CryptoError('redline crypto: invalid Huffman code in deflate stream')
  }

  // Length / distance base tables (RFC 1951 §3.2.5).
  const lengthBase = [
    3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131,
    163, 195, 227, 258,
  ]
  const lengthExtra = [
    0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0,
  ]
  const distBase = [
    1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537,
    2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577,
  ]
  const distExtra = [
    0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13,
    13,
  ]
  const codeLenOrder = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]

  function fixedHuffman(): {
    litLen: { counts: number[]; symbols: number[] }
    dist: { counts: number[]; symbols: number[] }
  } {
    const litLengths = new Array(288)
    for (let i = 0; i < 144; i++) litLengths[i] = 8
    for (let i = 144; i < 256; i++) litLengths[i] = 9
    for (let i = 256; i < 280; i++) litLengths[i] = 7
    for (let i = 280; i < 288; i++) litLengths[i] = 8
    const distLengths = new Array(30).fill(5)
    return { litLen: buildHuffman(litLengths), dist: buildHuffman(distLengths) }
  }

  function dynamicHuffman(): {
    litLen: { counts: number[]; symbols: number[] }
    dist: { counts: number[]; symbols: number[] }
  } {
    const hlit = getBits(5) + 257
    const hdist = getBits(5) + 1
    const hclen = getBits(4) + 4
    const clLengths = new Array(19).fill(0)
    for (let i = 0; i < hclen; i++) {
      clLengths[codeLenOrder[i]] = getBits(3)
    }
    const clHuff = buildHuffman(clLengths)
    const allLengths: number[] = []
    while (allLengths.length < hlit + hdist) {
      const sym = decodeSymbol(clHuff)
      if (sym < 16) {
        allLengths.push(sym)
      } else if (sym === 16) {
        const repeat = getBits(2) + 3
        const prev = allLengths[allLengths.length - 1]
        for (let i = 0; i < repeat; i++) allLengths.push(prev)
      } else if (sym === 17) {
        const repeat = getBits(3) + 3
        for (let i = 0; i < repeat; i++) allLengths.push(0)
      } else {
        const repeat = getBits(7) + 11
        for (let i = 0; i < repeat; i++) allLengths.push(0)
      }
    }
    return {
      litLen: buildHuffman(allLengths.slice(0, hlit)),
      dist: buildHuffman(allLengths.slice(hlit, hlit + hdist)),
    }
  }

  for (;;) {
    const isFinal = getBit()
    const blockType = getBits(2)

    if (blockType === 0) {
      // Stored / uncompressed block: align to byte boundary.
      bitCnt = 0
      if (bytePos + 4 > input.length) {
        throw new CryptoError('redline crypto: truncated stored block')
      }
      const len = input[bytePos] | (input[bytePos + 1] << 8)
      bytePos += 4 // skip LEN + NLEN
      for (let i = 0; i < len; i++) {
        emit(input[bytePos++])
      }
    } else {
      const tables = blockType === 1 ? fixedHuffman() : dynamicHuffman()
      if (blockType === 3) {
        throw new CryptoError('redline crypto: reserved deflate block type')
      }
      for (;;) {
        const sym = decodeSymbol(tables.litLen)
        if (sym === 256) break // end of block
        if (sym < 256) {
          emit(sym)
        } else {
          const li = sym - 257
          const length = lengthBase[li] + getBits(lengthExtra[li])
          const distSym = decodeSymbol(tables.dist)
          const distance = distBase[distSym] + getBits(distExtra[distSym])
          const start = out.length - distance
          if (start < 0) {
            throw new CryptoError('redline crypto: deflate back-reference before stream start')
          }
          for (let i = 0; i < length; i++) {
            emit(out[start + i])
          }
        }
      }
    }

    if (isFinal) break
  }

  return new Uint8Array(out)
}
